import { useSyncExternalStore } from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'dressup-theme';

const listeners = new Set<() => void>();
let preference: ThemePreference = readStoredPreference();

function readStoredPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    /* Private mode or blocked storage: fall back to the system preference. */
  }
  return 'system';
}

const systemQuery = () => window.matchMedia('(prefers-color-scheme: dark)');

export const resolveTheme = (pref: ThemePreference): ResolvedTheme =>
  pref === 'system' ? (systemQuery().matches ? 'dark' : 'light') : pref;

/** Paint the choice onto <html> and keep the browser UI colour in step. */
function apply(pref: ThemePreference) {
  const root = document.documentElement;
  if (pref === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', pref);

  const resolved = resolveTheme(pref);
  document
    .querySelectorAll('meta[name="theme-color"]')
    .forEach((tag) => tag.remove());

  const meta = document.createElement('meta');
  meta.name = 'theme-color';
  meta.content = resolved === 'dark' ? '#0e0e10' : '#f2f2f1';
  document.head.appendChild(meta);
}

function emit() {
  listeners.forEach((listener) => listener());
}

export function setThemePreference(next: ThemePreference) {
  preference = next;
  try {
    if (next === 'system') localStorage.removeItem(THEME_STORAGE_KEY);
    else localStorage.setItem(THEME_STORAGE_KEY, next);
  } catch {
    /* Preference is still applied for this session. */
  }
  apply(next);
  emit();
}

function subscribe(listener: () => void) {
  listeners.add(listener);

  // While following the system, react to the OS switching appearance.
  const query = systemQuery();
  const onSystemChange = () => {
    if (preference === 'system') {
      apply('system');
      emit();
    }
  };
  query.addEventListener('change', onSystemChange);

  return () => {
    listeners.delete(listener);
    query.removeEventListener('change', onSystemChange);
  };
}

const getSnapshot = () => `${preference}:${resolveTheme(preference)}`;

export function useTheme() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, () => 'system:light');
  const [pref, resolved] = snapshot.split(':') as [ThemePreference, ResolvedTheme];

  return {
    preference: pref,
    theme: resolved,
    isDark: resolved === 'dark',
    setPreference: setThemePreference,
    toggle: () => setThemePreference(resolved === 'dark' ? 'light' : 'dark'),
  };
}

// Keep the in-memory value aligned with whatever the inline head script painted.
if (typeof document !== 'undefined') apply(preference);
